import { useCallback, useEffect, useMemo } from 'react';
import { NumberParam, StringParam, useQueryParams, withDefault } from 'use-query-params';
import { useBlockPagerCore } from 'shared/utils/pagination';
import { SportType } from 'modules/core/domain/enums/sport-type';
import { TournamentListItem } from 'modules/tournaments/domain/interfaces/tournament-list-item';
import { ListTournamentsArgs, useListTournamentsQuery } from 'modules/tournaments/store/tournaments/tournaments-api';

const PER_PAGE_KEY = 'tournamentsV2PerPage';

const defaultFilters = {
  search: StringParam,
  phone: StringParam,
  sportType: StringParam,
  sortByCreatedAt: withDefault(StringParam, 'DESC'),
  page: withDefault(NumberParam, 1),
  perPage: withDefault(NumberParam, Number(localStorage.getItem(PER_PAGE_KEY)) || 9),
};

export function useTournamentsListV2() {
  const [filters, setFilters] = useQueryParams(defaultFilters);
  const page = filters.page ?? 1;
  const perPage = filters.perPage ?? 9;

  const stableFilters = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { page: _p, perPage: _pp, ...rest } = filters;
    return rest;
  }, [filters]);

  const changeFilter = useCallback(
    (changes: Partial<typeof filters>) => setFilters({ ...(changes as any) }),
    [setFilters],
  );

  const applyFilterImmediately = useCallback(
    (changes: Partial<typeof filters>) => setFilters({ ...(changes as any) }, 'replace'),
    [setFilters],
  );

  const pager = useBlockPagerCore<TournamentListItem, ListTournamentsArgs, typeof stableFilters>({
    useQueryHook: useListTournamentsQuery,
    page,
    perPage,
    filters: stableFilters,
    baseArgs: {},
    mapFiltersToArgs: (f) => ({
      text: (f?.search?.trim().length ?? 0) >= 3 ? f?.search?.trim() : undefined,
      phone: f?.phone ?? undefined,
      sportType: f?.sportType ? (f.sportType.toUpperCase() as SportType) : undefined,
      sortByCreatedAt: f?.sortByCreatedAt ?? undefined,
    }),
    rawPrefetchPages: 1,
    maxBackendLimit: 20,
    scopeEntity: 'tournaments-v2',
    scopeParts: {},
    getQueryOptions: () => ({ skip: false }),
  });

  useEffect(() => {
    if (pager.isLoading) return;
    if (pager.spec.provenNoMore && page > pager.spec.maxKnownPages) {
      applyFilterImmediately({ page: pager.spec.maxKnownPages } as any);
    }
  }, [pager.isLoading, pager.spec, page, applyFilterImmediately]);

  const changePerPage = useCallback(
    (v: number) => {
      localStorage.setItem(PER_PAGE_KEY, String(v));
      setFilters({ perPage: v, page: 1 } as any, 'replace');
    },
    [setFilters],
  );

  return {
    items: pager.itemsForPage,
    pages: pager.pages,
    isLoading: pager.isLoading,
    isError: pager.isError,
    changeFilter,
    changePerPage,
    filters: { ...filters, page, perPage },
  };
}
